import { useEffect, useLayoutEffect, useRef } from 'react'
import type { Chart } from 'klinecharts'

/**
 * Full-width lines drawn from a price alone: wherever the last bar is, they are drawn right, so
 * a truncated chart never distorts them and they stay on through the replay.
 */
const ALWAYS_VISIBLE = new Set(['levelLine', 'horizontalStraightLine'])

interface DrawingLike {
  name: string
  points?: Array<{ timestamp?: number | null } | null | undefined>
}

/**
 * Whether a drawing reaches past the last bar the chart holds, and so must be hidden.
 *
 * klinecharts resolves every timestamped point to the NEAREST bar (TimeScaleStore
 * .timestampToDataIndex is a binarySearchNearest), so while the replay cuts the canvas, anything
 * drawn later than the playhead would collapse onto the last bar — and a trend line crossing it
 * would take a new slope on every revealed bar. So the test is on the LATEST point: a drawing
 * appears once the replay has reached all of it.
 *
 * A point that has only a dataIndex never counts: that is what the engine creates when the user
 * draws in the blank area right of the last bar — which is exactly where they draw during a
 * replay — and it is drawn at its index, not snapped.
 */
export function isBeyondReveal(o: DrawingLike, lastRevealedMs: number): boolean {
  if (ALWAYS_VISIBLE.has(o.name)) return false
  for (const p of o.points ?? []) {
    const ts = p?.timestamp
    if (typeof ts === 'number' && Number.isFinite(ts) && ts > lastRevealedMs) return true
  }
  return false
}

export interface UseReplayDrawingVisibilityOptions {
  chart: Chart | null
  /** The user's drawings, by overlay id. */
  drawIdsRef: React.RefObject<Set<string>>
  /** ms timestamp of the newest bar on the chart while the replay cuts it; null when it doesn't. */
  lastRevealedMs: number | null
  /** Bumped whenever the drawing set or a drawing's points change. */
  version: number
  /** An overlay not to touch right now: the one being dragged, or drawn. */
  isExempt?: (id: string) => boolean
  /** Some drawings just went out of sight — so a selection on one of them can be dropped. */
  onHide?: (ids: string[]) => void
}

/**
 * Hide the user's drawings the replay has not reached yet, and bring each back as it does.
 *
 * Only visibility, and only engine-side: `serializeSymbolSlice` never reads an overlay's
 * `visible`, so nothing here is ever saved. (The trade-idea panel's own eye is a different
 * thing — `extendData.hidden`, which IS saved — and is left alone.) It only ever shows again what
 * it hid itself, and shows everything back when the replay stops cutting the chart or this
 * unmounts.
 */
export function useReplayDrawingVisibility({
  chart,
  drawIdsRef,
  lastRevealedMs,
  version,
  isExempt,
  onHide,
}: UseReplayDrawingVisibilityOptions): void {
  const hiddenRef = useRef<Set<string>>(new Set())
  const isExemptRef = useRef(isExempt)
  const onHideRef = useRef(onHide)
  useLayoutEffect(() => {
    isExemptRef.current = isExempt
    onHideRef.current = onHide
  })

  useLayoutEffect(() => {
    if (!chart) return
    const hidden = hiddenRef.current
    const want = new Set<string>()
    if (lastRevealedMs != null) {
      for (const id of drawIdsRef.current ?? []) {
        if (isExemptRef.current?.(id)) continue
        const o = chart.getOverlayById(id)
        if (o && isBeyondReveal(o as DrawingLike, lastRevealedMs)) want.add(id)
      }
    }
    const newlyHidden: string[] = []
    for (const id of want) {
      if (hidden.has(id)) continue
      chart.overrideOverlay({ id, visible: false })
      hidden.add(id)
      newlyHidden.push(id)
    }
    for (const id of [...hidden]) {
      if (want.has(id)) continue
      if (chart.getOverlayById(id)) chart.overrideOverlay({ id, visible: true })
      hidden.delete(id)
    }
    if (newlyHidden.length > 0) onHideRef.current?.(newlyHidden)
  }, [chart, drawIdsRef, lastRevealedMs, version])

  // Unmount, or a new chart: everything this hid comes back.
  useEffect(() => {
    const hidden = hiddenRef.current
    return () => {
      if (!chart) return
      for (const id of hidden) {
        if (chart.getOverlayById(id)) chart.overrideOverlay({ id, visible: true })
      }
      hidden.clear()
    }
  }, [chart])
}
